<template>
  <div class="tracking-page">
    <tracking-page-header
      title="API performance"
      subtitle="Every backend call during booking. Errors here are HTTP 500s only — 4xx still appear on the journey timeline."
      :from-date="fromDate"
      :to-date="toDate"
      :loading="loading"
      :chat-filters="chatFilters"
      :page-summary="pageSummary"
      @apply="fromDate = $event.fromDate; toDate = $event.toDate; loadData()"
      @refresh="loadData"
    />

    <div class="trk-kpi-grid">
      <div class="trk-kpi">
        <div class="trk-kpi-label">Total API calls</div>
        <div class="trk-kpi-value">{{ formatNumber(apis.totalCalls) }}</div>
        <div class="trk-kpi-help">Tracked during onboarding</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">HTTP 500s</div>
        <div class="trk-kpi-value">{{ formatNumber(apis.totalErrors) }}</div>
        <div class="trk-kpi-help">{{ formatPct(apis.overallErrorRatePercent) }} of calls</div>
      </div>
      <div class="trk-kpi">
        <div class="trk-kpi-label">Endpoints tracked</div>
        <div class="trk-kpi-value">{{ formatNumber((apis.endpoints || []).length) }}</div>
      </div>
    </div>

    <div class="trk-panel">
      <div class="trk-panel-head">
        <div>
          <h2>Endpoint breakdown</h2>
          <p>Sorted by call volume — click a session to see the full timeline</p>
        </div>
      </div>
      <div class="trk-panel-body p-0">
        <table class="table trk-table mb-0" v-trk-resizable="'api-endpoints'">
          <thead>
            <tr>
              <th>Endpoint</th>
              <th>Method</th>
              <th>Calls</th>
              <th>HTTP 500s</th>
              <th>Avg time</th>
              <th>P95 time</th>
              <th>Slow (&gt;2s)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in (apis.endpoints || [])" :key="row.apiPath + row.httpMethod">
              <td><code>{{ row.apiPath }}</code></td>
              <td><span class="trk-pill">{{ row.httpMethod }}</span></td>
              <td>{{ formatNumber(row.callCount) }}</td>
              <td>
                <span :class="row.errorCount > 0 ? 'trk-pill bad' : 'trk-pill'">
                  {{ formatNumber(row.errorCount) }} ({{ formatPct(row.errorRatePercent) }})
                </span>
              </td>
              <td>{{ formatMs(row.avgDurationMs) }}</td>
              <td>{{ formatMs(row.p95DurationMs) }}</td>
              <td>
                <span :class="row.slowCallCount > 0 ? 'trk-pill watch' : 'trk-pill'">
                  {{ formatNumber(row.slowCallCount) }}
                </span>
              </td>
            </tr>
            <tr v-if="!(apis.endpoints || []).length">
              <td colspan="7">
                <div class="trk-empty">
                  <strong>No API calls yet</strong>
                  Enable patient tracking and wait for onboarding traffic.
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import TrackingPageHeader from './TrackingPageHeader.vue';
import { defaultDateRange, formatNumber, formatPct, rangeParams } from './trackingShared';

export default {
  name: 'TrackingApis',
  components: { TrackingPageHeader },
  data() {
    const range = defaultDateRange(7);
    return {
      fromDate: range.fromDate,
      toDate: range.toDate,
      apis: {},
      loading: false
    };
  },
  created() { this.loadData(); },
  computed: {
    chatFilters() {
      return rangeParams(this.fromDate, this.toDate);
    },
    pageSummary() {
      const endpoints = (this.apis.endpoints || []).length;
      return `${this.formatNumber(this.apis.totalCalls)} API calls, ${this.formatNumber(this.apis.totalErrors)} failed (${this.formatPct(this.apis.overallErrorRatePercent)}), ${endpoints} endpoints shown`;
    }
  },
  methods: {
    formatNumber,
    formatPct,
    formatMs(ms) {
      const value = Number(ms || 0);
      if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}s`;
      }
      return `${Math.round(value)}ms`;
    },
    loadData() {
      this.loading = true;
      this.$onboardingTrackingService.getApis(rangeParams(this.fromDate, this.toDate))
        .then((res) => { this.apis = (res && res.data) || {}; })
        .finally(() => { this.loading = false; });
    }
  }
};
</script>
